/// <amd-module name="Wit/LinkedWorkItemsDropdown/Components/LinkedWorkItemsCreatePullRequest" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { IMessageCardProps, MessageCardSeverity } from "VSSUI/MessageCard";
import * as Resources from "Wit/LinkedWorkItemsDropdown/Resources";
import { IArtifactLink } from "../LinkedWorkItemsService";
import { ArtifactCompleteListener, getPullRequestArtifactLink } from "../Util/Artifact";
import { ILinkedWorkItemsDropdownProps, LinkedWorkItemsDropdown } from "./LinkedWorkItemsDropdown";

interface ILinkedWorkItemsCreatePullRequestService extends IVssService {
    getAssociatedWorkItems(pullRequestCreationId: number): number[] | undefined;
    setAssociatedWorkItems(pullRequestCreationId: number, workItemIds: number[]): void;
}

class LinkedWorkItemsCreatePullRequestService extends VssService implements ILinkedWorkItemsCreatePullRequestService {
    public workItemsByPullRequest: { [creationId: number]: number[] } = {};

    public getAssociatedWorkItems(pullRequestCreationId: number): number[] | undefined {
        return this.workItemsByPullRequest[pullRequestCreationId];
    }

    public setAssociatedWorkItems(pullRequestCreationId: number, workItemIds: number[]): void {
        this.workItemsByPullRequest[pullRequestCreationId] = workItemIds;
    }
}

Services.add("ILinkedWorkItemsCreatePullRequestService", { serviceFactory: LinkedWorkItemsCreatePullRequestService });

interface GitPullRequest {
    pullRequestId: number;
    repository: { id: string; project: { id: string } };
}

type PullRequestCreateListener = (pullRequest: GitPullRequest) => Promise<void> | void;
interface ICreatePullRequestOverviewExtensionContext {
    addPullRequestCreateListener: (listener: PullRequestCreateListener) => void;
    commits: { commit: { commitId: string } }[];
    pullRequestCreationId: number;
    repository: {
        id: string;
        name: string;
    };
    sourceVersion: string;
    targetVersion: string;
}

const associatedWorkItemsDataProviderId = "ms.vss-code-web.associated-workitems-data-provider";
interface IAssociatedWorkItem {
    id: number;
}

const maxWorkItemsForWarning = 10;

interface ILinkedWorkItemsCreatePullRequestProps {
    createPullRequestContext: ICreatePullRequestOverviewExtensionContext;
}

class LinkedWorkItemsCreatePullRequest extends VssComponent<ILinkedWorkItemsCreatePullRequestProps> {
    private artifactListener: ArtifactCompleteListener | undefined;
    private message = new ObservableValue<IMessageCardProps & { children: React.ReactNode } | undefined>(undefined);
    private dropdownRef = React.createRef<LinkedWorkItemsDropdown>();

    public render(): JSX.Element {
        const { createPullRequestContext } = this.props;

        const linkedWorkItemsService = this.context.pageContext.getService<ILinkedWorkItemsCreatePullRequestService>(
            "ILinkedWorkItemsCreatePullRequestService"
        );

        const linkedWorkItemsProps: ILinkedWorkItemsDropdownProps = {
            artifactChangesContext: {
                addArtifactCompleteListener: (listener: (artifactLinks: IArtifactLink[]) => Promise<void> | void): void => {
                    this.artifactListener = listener;
                    createPullRequestContext.addPullRequestCreateListener(this.linkWorkItems);
                }
            },
            message: this.message,
            onSelectedWorkItemsChanged: workItems =>
                linkedWorkItemsService.setAssociatedWorkItems(createPullRequestContext.pullRequestCreationId, workItems.map(w => w.id))
        };

        const workItemIds = linkedWorkItemsService.getAssociatedWorkItems(createPullRequestContext.pullRequestCreationId);

        if (workItemIds) {
            linkedWorkItemsProps.initialWorkItems = workItemIds;
        } else {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            linkedWorkItemsProps.initialWorkItems = contributionService
                .getDataAsync<IAssociatedWorkItem[]>(associatedWorkItemsDataProviderId, {
                    repositoryId: createPullRequestContext.repository.id,
                    versions: [createPullRequestContext.sourceVersion, ...createPullRequestContext.commits.map(c => "GC" + c.commit.commitId)]
                })
                .then(result => {
                    const ids = result ? result.map(w => w.id) : [];
                    if (ids.length >= maxWorkItemsForWarning) {
                        this.message.value = {
                            buttonProps: [
                                {
                                    onClick: () => {
                                        if (this.dropdownRef.current) {
                                            this.dropdownRef.current.removeWorkItems(ids);
                                            this.message.value = undefined;
                                        }
                                    },
                                    text: Resources.Clear,
                                    tooltipProps: { text: Resources.ClearPullRequestWorkItemsTooltip }
                                }
                            ],
                            children: format(Resources.CreatePullRequestWarningFormat, ids.length),
                            onDismiss: () => (this.message.value = undefined),
                            severity: MessageCardSeverity.Warning
                        };
                    }
                    return ids;
                });
        }

        return <LinkedWorkItemsDropdown {...linkedWorkItemsProps} ref={this.dropdownRef} />;
    }

    private linkWorkItems = async (pullRequest: GitPullRequest): Promise<void> => {
        if (this.artifactListener) {
            try {
                return await this.artifactListener([
                    getPullRequestArtifactLink(pullRequest.repository.project.id, pullRequest.repository.id, pullRequest.pullRequestId)
                ]);
            } catch (ex) {
                throw new Error(format(Resources.PullRequestLinkError, ex.message));
            }
        }
    };
}

Components.add("WIT.LinkedWorkItemsCreatePullRequest", LinkedWorkItemsCreatePullRequest);
